import { ZodError } from 'zod';
import { errorResponseSchema, validationErrorResponseSchema, } from './contracts/errors';
/**
 * Base API error class with status code and response data
 */
export class ApiError extends Error {
    constructor(status, data, message, requestUrl) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.data = data;
        this.requestUrl = requestUrl;
        // Try to parse the response as an error response
        try {
            this.response = errorResponseSchema.parse(data);
        }
        catch {
            // If parsing fails, response will be undefined
        }
    }
}
/**
 * Validation error for 400 Bad Request responses
 */
export class ValidationError extends ApiError {
    constructor(status, data, message, requestUrl) {
        super(status, data, message, requestUrl);
        this.name = 'ValidationError';
        // Try to parse as validation error response
        try {
            this.validationResponse = validationErrorResponseSchema.parse(data);
        }
        catch {
            // If parsing fails, validationResponse will be undefined
        }
    }
}
/**
 * Unauthorized error for 401 Unauthorized responses
 */
export class UnauthorizedError extends ApiError {
    constructor(status, data, message, requestUrl) {
        super(status, data, message, requestUrl);
        this.name = 'UnauthorizedError';
    }
}
/**
 * Not found error for 404 Not Found responses
 */
export class NotFoundError extends ApiError {
    constructor(status, data, message, requestUrl) {
        super(status, data, message, requestUrl);
        this.name = 'NotFoundError';
    }
}
/**
 * Server error for 5xx responses
 */
export class ServerError extends ApiError {
    constructor(status, data, message, requestUrl) {
        super(status, data, message, requestUrl);
        this.name = 'ServerError';
    }
}
/**
 * Schema parse error for when API responses don't match expected schemas.
 * Wraps ZodError with a human-readable message while preserving the original error.
 */
export class SchemaParseError extends Error {
    constructor(zodError, data) {
        const issues = zodError.issues
            .map(issue => `${issue.path.join('.')}: ${issue.message}`)
            .join(', ');
        super(`Invalid API response: ${issues}`);
        this.name = 'SchemaParseError';
        this.zodError = zodError;
        this.data = data;
    }
}
/**
 * Safely parses data with a Zod schema, throwing a user-friendly SchemaParseError on failure.
 * Use this instead of calling schema.parse() directly to ensure consistent error handling.
 *
 * @param schema - The Zod schema to validate against
 * @param data - The data to parse
 * @returns The parsed and validated data
 * @throws SchemaParseError if validation fails
 */
export function parseSchema(schema, data) {
    try {
        return schema.parse(data);
    }
    catch (error) {
        if (error instanceof ZodError) {
            throw new SchemaParseError(error, data);
        }
        throw error;
    }
}
/**
 * Creates an appropriate error instance based on the status code
 */
export function createApiError(status, data, message, requestUrl) {
    if (status === 400) {
        return new ValidationError(status, data, message, requestUrl);
    }
    else if (status === 401) {
        return new UnauthorizedError(status, data, message, requestUrl);
    }
    else if (status === 404) {
        return new NotFoundError(status, data, message, requestUrl);
    }
    else if (status >= 500) {
        return new ServerError(status, data, message, requestUrl);
    }
    else {
        return new ApiError(status, data, message, requestUrl);
    }
}
//# sourceMappingURL=errors.js.map